export type EventStatus =
  | "DRAFT"
  | "IN_REVIEW"
  | "REGISTRATION_OPEN"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED"
  | "DENIED"
  | "ARCHIVED"
  | "SUSPENDED";

export const EVENT_STATUS_DRAFT: EventStatus = "DRAFT";
export const EVENT_STATUS_IN_REVIEW: EventStatus = "IN_REVIEW";

type StatusDisplay = {
  label: string;
  tone: string;
};

const eventStatusMap: Record<EventStatus, StatusDisplay> = {
  DRAFT: { label: "Draft", tone: "neutral" },
  IN_REVIEW: { label: "In review", tone: "info" },
  REGISTRATION_OPEN: { label: "Active", tone: "success" },
  IN_PROGRESS: { label: "Active", tone: "success" },
  COMPLETED: { label: "Completed", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "critical" },
  DENIED: { label: "Denied", tone: "critical" },
  ARCHIVED: { label: "Archived", tone: "neutral" },
  SUSPENDED: { label: "Suspended", tone: "critical" },
};

function isEventStatus(status: string): status is EventStatus {
  return status in eventStatusMap;
}

export function normalizeEventStatus(status: string): EventStatus | null {
  const statusKey = status.trim().toUpperCase();

  const normalizedStatus =
    statusKey === "REVIEW"
      ? EVENT_STATUS_IN_REVIEW
      : statusKey === "ACTIVE"
        ? "IN_PROGRESS"
        : statusKey;

  return isEventStatus(normalizedStatus) ? normalizedStatus : null;
}

export function isDeniedEventStatus(status: string): boolean {
  return normalizeEventStatus(status) === "DENIED";
}

export function isEventInReview(status: string): boolean {
  return normalizeEventStatus(status) === EVENT_STATUS_IN_REVIEW;
}

export function canViewPublicEventLandingPage(status: string): boolean {
  const normalizedStatus = normalizeEventStatus(status);

  return (
    normalizedStatus === "REGISTRATION_OPEN" ||
    normalizedStatus === "IN_PROGRESS" ||
    normalizedStatus === "COMPLETED"
  );
}

export function canOrganizerAccessSignedAgreement(status: string): boolean {
  return normalizeEventStatus(status) === "REGISTRATION_OPEN";
}

export function getEventStatusDisplay(status: string): StatusDisplay {
  if (!isEventStatus(status)) {
    return { label: status, tone: "neutral" };
  }

  return eventStatusMap[status];
}

export function getEventStatusToneClass(status: string): string {
  const { tone } = getEventStatusDisplay(status);
  return `events-tone events-tone--${tone}`;
}
